import { Controller } from "@hotwired/stimulus"

// The watching screen's poller (U3): every ten seconds (and once immediately)
// it reads /invest/watching/status and drives the page's one-visible-section
// state machine. Terminal states stop the polling; a fetch that fails changes
// nothing and the next tick tries again — an unreadable status never breaks
// the screen.
const POLL_INTERVAL_MS = 10_000
const STALL_AFTER_MS = 90 * 1000
const GIVE_UP_AFTER_MS = 15 * 60 * 1000
const FALLBACK_BEAT_MS = 1500

const LEAD = {
  starting: "Setting up your first buy. Nothing else for you to do.",
  executing: "Making your purchases now. Nothing else for you to do.",
  retrying: "The price wasn't fair yet, so we'll try again later today. Nothing was taken.",
  shortfall: "Your first buy is waiting on a top-up.",
  fallback: "One more step and this buy is done.",
  paused: "Nothing is happening right now.",
  on_hold: "Your plan needs a fresh approval.",
  complete: "All done.",
  partial: "Nearly all done.",
  missed: "Not this time."
}

const SECTIONS = ["shortfall", "paused", "onHold", "stalled", "fallback", "complete", "partial", "missed"]
const SECTION_FOR_STATE = {
  shortfall: "shortfall", paused: "paused", on_hold: "onHold",
  fallback: "fallback", complete: "complete", partial: "partial", missed: "missed"
}
// "paused" is deliberately not terminal: the screen promises to pick the buy
// up when the operator resumes, so polling must keep running through a pause.
const TERMINAL_STATES = ["on_hold", "complete", "partial", "missed"]

export default class extends Controller {
  static values = { url: String, investPath: String, watchingPath: String }
  static targets = ["lead", "legStatus", "legRow", "shortfall", "shortfallAmount",
    "paused", "onHold", "stalled", "stalledLink", "fallback",
    "complete", "partial", "missed", "next", "nextLink"]

  connect() {
    this.startedAt = Date.now()
    this.generation = 0
    this.check()
    this.timer = setInterval(() => this.check(), POLL_INTERVAL_MS)
  }

  disconnect() {
    this.stop()
  }

  async check() {
    // A slow response must never overwrite a newer one: each check bumps the
    // generation, and a response whose generation has been superseded is dropped.
    const generation = ++this.generation
    try {
      const response = await fetch(this.urlValue, { headers: { Accept: "application/json" } })
      if (generation !== this.generation) return
      if (!response.ok) return
      const payload = await response.json()
      if (generation !== this.generation) return
      this.render(payload)
    } catch {
      // Fail open: the next tick tries again.
    }
  }

  render(payload) {
    this.updateRows(payload.legs || [])
    const state = payload.state

    if (state === "starting") return this.renderStarting()

    this.setLead(LEAD[state])

    if (state === "shortfall" && this.hasShortfallAmountTarget && payload.shortfall_usdc) {
      this.shortfallAmountTarget.textContent = payload.shortfall_usdc
    }
    // A pause is indefinite by nature: keep polling, and keep resetting the
    // give-up clock so a resume never lands on an already-expired deadline.
    if (state === "paused") this.startedAt = Date.now()
    if (TERMINAL_STATES.includes(state) || state === "fallback") this.stop()

    this.showSection(SECTION_FOR_STATE[state] || null)

    if (state === "fallback" && !this.fallbackTimer) {
      this.fallbackTimer = setTimeout(() => window.location.assign(this.investPathValue), FALLBACK_BEAT_MS)
    }
    if (TERMINAL_STATES.includes(state)) this.offerNextPeriod(payload.newer_period)
  }

  // Still no cycle: calm at first, honest after 90 seconds, and after 15
  // minutes we stop asking and hand over a link to the plan page instead.
  renderStarting() {
    const elapsed = Date.now() - this.startedAt
    if (elapsed < STALL_AFTER_MS) {
      this.setLead(LEAD.starting)
      return this.showSection(null)
    }

    this.showSection("stalled")
    if (elapsed >= GIVE_UP_AFTER_MS) {
      if (this.hasStalledLinkTarget) this.stalledLinkTarget.hidden = false
      this.stop()
    }
  }

  updateRows(legs) {
    for (const leg of legs) {
      const element = this.legStatusTargets.find((target) => target.dataset.bag === leg.bag)
      if (element && element.textContent.trim() !== leg.text) element.textContent = leg.text
      const row = this.legRowTargets.find((target) => target.dataset.bag === leg.bag)
      if (row && leg.status && row.dataset.legState !== leg.status) row.dataset.legState = leg.status
    }
  }

  // Demo mode's second buy: a finished watch can hand over to the next one.
  offerNextPeriod(newerPeriod) {
    if (newerPeriod == null || !this.hasNextTarget) return
    this.nextLinkTarget.href = `${this.watchingPathValue}?period=${newerPeriod}`
    this.nextTarget.hidden = false
  }

  showSection(name) {
    for (const section of SECTIONS) {
      const property = `${section}Target`
      if (this[`has${section[0].toUpperCase()}${section.slice(1)}Target`]) {
        this[property].hidden = section !== name
      }
    }
  }

  setLead(text) {
    if (text && this.hasLeadTarget && this.leadTarget.textContent.trim() !== text) {
      this.leadTarget.textContent = text
    }
  }

  stop() {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
    this.element.dataset.watchStopped = "" // the Live dot stops breathing
    if (this.fallbackTimer) clearTimeout(this.fallbackTimer)
    this.fallbackTimer = null
  }
}
